import { useRouter } from "next/router";
import React, { useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";

import {
  PriceCalendarOptions,
  PriceCalendarSelectionMode,
} from "shared/lib/priceCalendar";

import CalendarMonth from "./CalendarMonth";
import type { PriceCalendarCache } from "./CalendarMonth";

const CLOSE_MODAL_TIMEOUT_MS = 250;

interface CalendarProps {
  date: string;
  onChange: (any, string) => void;
  typeSearch: string;
  disabledTillDate: string;
  disabledAfterDate?: string;
  close: () => void;
  priceCalendar?: PriceCalendarOptions;
  rangeSelectionEnabled?: boolean;
  departureDate?: string;
  returnDate?: string;
  onDepartureChange?: (value: string) => void;
  onReturnChange?: (value: string) => void;
  initialSelectionMode?: PriceCalendarSelectionMode;
  priceCalendarCache?: React.MutableRefObject<PriceCalendarCache>;
}

export default function Calendar({
  date,
  onChange,
  typeSearch,
  close,
  disabledTillDate,
  disabledAfterDate,
  priceCalendar,
  rangeSelectionEnabled,
  departureDate,
  returnDate,
  onDepartureChange,
  onReturnChange,
  initialSelectionMode = PriceCalendarSelectionMode.OUTBOUND,
  priceCalendarCache,
}: CalendarProps) {
  const router = useRouter();
  const [rangeSelectionMode, setRangeSelectionMode] = useState(
    initialSelectionMode
  );

  useEffect(() => {
    setRangeSelectionMode(initialSelectionMode);
  }, [initialSelectionMode]);

  const isHotelSearchForm =
    router.query &&
    router.query.typeSearch &&
    router.query.typeSearch === "hotels";
  const titleTypeSearch = rangeSelectionEnabled
    ? rangeSelectionMode === PriceCalendarSelectionMode.OUTBOUND
      ? "DEPARTURE"
      : "RETURN"
    : typeSearch;

  return (
    <div className="header-search-form-calendar">
      <span
        className="header-search-form-calendar-initial-focus"
        style={{ outline: "none" }}
        tabIndex={-1}
      />
      <div className="header-search-form-calendar-inner">
        <div className="header-search-form-calendar-months">
          <div className="header-search-form-calendar-title">
            {titleTypeSearch === "DEPARTURE" ? (
              isHotelSearchForm ? (
                <FormattedMessage id="SearchForm.hotels.selectDepartureDate" />
              ) : (
                <FormattedMessage id="SearchForm.selectDepartureDate" />
              )
            ) : isHotelSearchForm ? (
              <FormattedMessage id="SearchForm.hotels.selectReturnDate" />
            ) : (
              <FormattedMessage id="SearchForm.selectReturnDate" />
            )}
            :
          </div>
          <div className="header-search-form-calendar-months-month">
            <CalendarMonth
              value={rangeSelectionEnabled ? departureDate || date : date}
              onChange={(e) => {
                onChange(e, typeSearch);
                if (!rangeSelectionEnabled) {
                  setTimeout(() => {
                    close();
                  }, CLOSE_MODAL_TIMEOUT_MS);
                }
              }}
              disabledTillDate={disabledTillDate}
              disabledAfterDate={disabledAfterDate}
              priceCalendar={priceCalendar}
              rangeSelectionEnabled={rangeSelectionEnabled}
              rangeDepartureDate={departureDate}
              rangeReturnDate={returnDate}
              onRangeDepartureChange={(value) => onDepartureChange?.(value)}
              onRangeReturnChange={(value) => onReturnChange?.(value)}
              onSelectionModeChange={setRangeSelectionMode}
              initialSelectionMode={initialSelectionMode}
              priceCalendarCache={priceCalendarCache}
            />
          </div>
          {rangeSelectionEnabled && (
            <div className="header-search-form-calendar-actions">
              <button
                className="primary-button header-search-form-calendar-done"
                disabled={!departureDate || !returnDate}
                onClick={close}
                type="button"
              >
                <FormattedMessage id="PropertiesSelect.done" />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
